import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Loader2, BookOpen, Download, Clock, FileText, Star,
  ChevronDown, ChevronUp, ShoppingBag, Library
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MyLibrary = () => {
  const [ebooks, setEbooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    fetchMyEbooks();
  }, []);

  const fetchMyEbooks = async () => {
    try {
      const res = await axios.get(`${API}/my-ebooks`);
      setEbooks(res.data);
    } catch (err) {
      console.error('Failed to load library:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (ebook) => {
    setDownloading(ebook.id);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/ebooks/${ebook.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Download failed');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = ebook.original_filename || `${ebook.name}.${ebook.file_format || 'pdf'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Download started!');
      fetchMyEbooks();
    } catch (err) {
      toast.error(err.message || 'Download failed');
    } finally {
      setDownloading(null);
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatDateTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 flex justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="my-library-page">
      {/* Header */}
      <div className="mb-10">
        <div className="flex items-center gap-3 mb-2">
          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Library className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="font-serif text-3xl font-bold text-foreground" data-testid="library-title">My Library</h1>
            <p className="text-muted-foreground text-sm">
              {ebooks.length === 0 ? 'Your bookshelf is empty' : `${ebooks.length} title${ebooks.length !== 1 ? 's' : ''} in your collection`}
            </p>
          </div>
        </div>
      </div>

      {ebooks.length === 0 ? (
        <div className="text-center py-20">
          <div className="relative inline-block mb-6">
            <div className="h-32 w-32 rounded-full bg-muted/50 flex items-center justify-center mx-auto">
              <BookOpen className="h-16 w-16 text-muted-foreground/40" />
            </div>
          </div>
          <h2 className="font-serif text-2xl font-bold text-foreground mb-3">Your library is empty</h2>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">
            When you purchase e-books or digital guides, they'll appear here for you to download anytime.
          </p>
          <Link to="/ebooks">
            <Button className="btn-accent" data-testid="browse-ebooks-btn">
              <ShoppingBag className="h-4 w-4 mr-2" />
              Browse E-books
            </Button>
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {ebooks.map((ebook) => (
            <Card
              key={ebook.id}
              className="overflow-hidden hover:shadow-md transition-shadow"
              data-testid={`library-item-${ebook.id}`}
            >
              <CardContent className="p-0">
                <div className="flex">
                  {/* Cover */}
                  <Link to={`/ebooks/${ebook.id}`} className="flex-shrink-0">
                    <div className="w-28 sm:w-36 h-40 sm:h-48 bg-gradient-to-b from-muted to-muted/50 flex items-center justify-center overflow-hidden">
                      {ebook.cover_url ? (
                        <img src={ebook.cover_url} alt={ebook.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="flex flex-col items-center gap-2 text-muted-foreground">
                          <BookOpen className="h-10 w-10" />
                          <span className="text-xs font-medium">{ebook.file_format?.toUpperCase()}</span>
                        </div>
                      )}
                    </div>
                  </Link>

                  {/* Details */}
                  <div className="flex-1 p-4 sm:p-5 flex flex-col justify-between min-w-0">
                    <div>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <Link to={`/ebooks/${ebook.id}`}>
                            <h3 className="font-serif font-bold text-lg text-foreground hover:text-primary transition-colors line-clamp-1" data-testid="library-book-title">
                              {ebook.name}
                            </h3>
                          </Link>
                          {ebook.author && (
                            <p className="text-sm text-muted-foreground mt-0.5">by {ebook.author}</p>
                          )}
                        </div>
                        {ebook.is_featured && (
                          <Badge className="bg-amber-500 text-white text-xs flex-shrink-0">
                            <Star className="h-3 w-3 fill-current mr-1" />Featured
                          </Badge>
                        )}
                      </div>

                      {/* Meta info */}
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <FileText className="h-3.5 w-3.5" />
                          {ebook.file_format?.toUpperCase()} {formatSize(ebook.file_size) && `- ${formatSize(ebook.file_size)}`}
                        </div>
                        {ebook.pages > 0 && (
                          <span>{ebook.pages} pages</span>
                        )}
                        <div className="flex items-center gap-1">
                          <Clock className="h-3.5 w-3.5" />
                          Purchased {formatDate(ebook.purchased_at)}
                        </div>
                        <div className="flex items-center gap-1">
                          <Download className="h-3.5 w-3.5" />
                          {ebook.user_downloads || 0} download{ebook.user_downloads !== 1 ? 's' : ''}
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-3 mt-4">
                      <Button
                        className="btn-accent"
                        size="sm"
                        onClick={() => handleDownload(ebook)}
                        disabled={downloading === ebook.id}
                        data-testid={`download-${ebook.id}`}
                      >
                        {downloading === ebook.id ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                        ) : (
                          <Download className="h-4 w-4 mr-1.5" />
                        )}
                        {downloading === ebook.id ? 'Downloading...' : `Download ${ebook.file_format?.toUpperCase()}`}
                      </Button>

                      {ebook.download_history && ebook.download_history.length > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-xs text-muted-foreground"
                          onClick={() => setExpandedId(expandedId === ebook.id ? null : ebook.id)}
                          data-testid={`history-toggle-${ebook.id}`}
                        >
                          History
                          {expandedId === ebook.id ? (
                            <ChevronUp className="h-3 w-3 ml-1" />
                          ) : (
                            <ChevronDown className="h-3 w-3 ml-1" />
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Download History */}
                {expandedId === ebook.id && ebook.download_history && ebook.download_history.length > 0 && (
                  <div className="border-t bg-muted/30 px-5 py-3" data-testid={`download-history-${ebook.id}`}>
                    <p className="text-xs font-medium text-muted-foreground mb-2">Download History</p>
                    <div className="space-y-1">
                      {ebook.download_history.map((dt, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Download className="h-3 w-3" />
                          <span>{formatDateTime(dt)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Browse more */}
      {ebooks.length > 0 && (
        <div className="text-center mt-10 pt-8 border-t">
          <p className="text-muted-foreground mb-3">Looking for more reads?</p>
          <Link to="/ebooks">
            <Button variant="outline" data-testid="browse-more-btn">
              <BookOpen className="h-4 w-4 mr-2" />
              Browse More E-books
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
};

export default MyLibrary;
